import ComplaintStatusPill from '@/components/Dashboard/Order/components/ComplaintStatusPill.vue';
import Select from '@/components/Shared/Select.vue';
import { canViewRecordActivity } from '@/composables/useAuditLog';
import { isOrderActionCode, type ElsaActionRequest } from '@/utils/elsaActions';
import Tooltip from '@/components/Shared/Tooltip.vue';
import {
  can,
  cleanName,
  CustimooOrderFlowStatuses,
  formatDate,
  formatShortDate,
  getComplaintsArray,
  logError,
  mergeComplaintsOntoOrderDetail,
  setPageTitle,
  timeAgo,
} from '@/composables';
import { useVueQuery } from '@/composables/useVueQuery';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { inHandTitle, inHandTone } from '@/utils/inHandDate';
import {
  DashboardItem,
  DashboardRequest,
  FactoryProduct,
  OrderAdminUser,
  OrderDetail,
  OrderDetailItem,
  Urgency,
} from '@/models';
import { useMainStore } from '@/store';
import { getImpersonation } from '@/composables/impersonation';
import { useDashboardStore } from '@/store/dashboard';
import { compactDuration } from '@/utils/compactDuration';
import { statusTone, TONE_DOT, TONE_TEXT } from '@/utils/orderStatusTone';
import { rowForRestoredPane } from '@/utils/dashboardRestore';
import { buildExportOrderParams } from '@/utils/exportOrderParams';
import { useColumnWidths } from '@/composables/useColumnWidths';
import {
  factoryDeadline,
  factoryDeadlineTitle,
  factoryDeadlineTone,
} from '@/utils/factoryDeadline';
import {
  applyColumnPreference,
  COLUMN_PREF_KEY,
  defaultHiddenLabels,
  effectiveHiddenColumns,
  hasColumnCustomisation,
  hideableLabels,
  pageSizeForViewport,
  readHiddenColumns,
  shownColumnsKey,
  sidebarShouldBeOpen,
  splitHiddenColumns,
  sidebarStateAfterPaneClose,
  writeHiddenColumns,
} from '@/utils/tablePreferences';
import { ChatBubbleBottomCenterTextIcon } from '@heroicons/vue/24/outline';
import { ChevronDownIcon, KeyIcon } from '@heroicons/vue/24/solid';
import { createPopper } from '@popperjs/core';
import { useQueryClient } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { computed, ComputedRef, h, nextTick, ref, VNode, watch } from 'vue';
import { formatTimestampWithBrowserTimezone } from '@/composables/useTimeZoneFormatDate';
import {
  formatInvoiceStatusLabel,
  invoiceCompositeStatusPillClass,
} from '@/utils/invoiceCompositeStatusDisplay';
import orderHoldIcon from '@/assets/img/images/stop.svg';
import { useRoute, useRouter } from 'vue-router';
import { showConfirm, showToast } from './swal';
import {
  activateTab,
  activeTab as activeOrderTab,
  closeTab,
  openTab,
  readTabs,
  setDirty,
  setInnerTab,
  tabKey,
  undoReplace,
  writeTabs,
  type OrderTab,
  type OrderTabsState,
} from '@/utils/orderTabs';
import { elsaAllowed } from '@/utils/elsaAccess';
import { isOfferedForEveryFactory } from '@/utils/orderAdminRoles';
import {
  makeReorderMessage as sharedMakeReorderMessage,
  type ReorderFactoryProduct,
} from '@/utils/reorderMessage';
declare global {
  interface Window {
    virtualReference?: {
      getBoundingClientRect: () => DOMRect;
    };
  }
}
interface OrderPage {
  items: DashboardItem[];
  total: number;
  nextPage?: number;
}

interface Column {
  key: string;
  label: string;
  sortable?: boolean;
  sortKey?: string;
  renderer?: (value: any, row: any) => string | VNode | number;
}

export const useDashboard = () => {
  const store = useMainStore();
  const dashboardStore = useDashboardStore();
  const appURL = import.meta.env.VITE_VUE2_APP_URL;

  const {
    dashboardData,
    dashboardTabs,
    permissionLoaded,
    orderAdmins,
    dashboardQuickFilters,
    loading,
    sidebarOpen,
  } = storeToRefs(store);

  const { modeTabs, currentMode, orderList } = storeToRefs(dashboardStore);

  const datatable = ref(null);

  const queryClient = useQueryClient();

  const invalidateOrderActivityLogQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ['order-activity-logs'] });
    await queryClient.invalidateQueries({ queryKey: ['order-activity-log-detail'] });
  };

  const showDashboardActions = ref<boolean>(false);

  const currentTab = ref('all_items');
  const paneDefaultTab = ref<string>(
    localStorage.getItem('activeDetailsTab') ??
      (elsaAllowed(store.user?.roles, store.features) ? 'ask_elsa' : 'timeline')
  );
  const selectedFilter = ref<string | null>(null);
  const createManualOrder = ref<boolean>(false);
  const assignFactoryModal = ref<boolean>(false);
  const selectedOrderDetails = ref<OrderDetail>(null);
  const isSidepaneLoading = ref<boolean>(false);

  /*
   * AB#21279 — orders open in the pane, as tabs.
   *
   * `orderTabs` is the set and which one is active; the rules live in
   * utils/orderTabs. `selectedOrderDetails` and `currentRow` keep meaning what
   * they mean everywhere else in this file — "the order the pane is showing" —
   * and are switched when the active tab changes. Each tab's details and row
   * are cached so switching is instant; a tab revisited after DETAIL_TTL_MS
   * refetches.
   */
  // Read for THIS user: a set another person left in this browser (an admin
  // impersonating a merchant, a shared machine) is forgotten, not shown.
  const orderTabs = ref<OrderTabsState>(readTabs(localStorage, store.user?.id ?? null));
  const detailsByTab = new Map<string, { details: OrderDetail; fetchedAt: number }>();
  const rowsByTab = new Map<string, DashboardItem>();
  const DETAIL_TTL_MS = 60_000;
  /** The tab a sixth order displaced, kept until Undo expires. */
  const lastReplacedTab = ref<{ tab: OrderTab; byKey: string } | null>(null);
  let lastReplacedTimer: ReturnType<typeof setTimeout> | null = null;
  const paneExpanded = ref<boolean>(localStorage.getItem('dashboardPaneExpanded') === 'true');
  const priceBreakdownModal = ref<boolean>(false);
  const selectedOrderItem = ref<DashboardItem>(null);
  const selectedUrgentOrderFilter = ref<string | null>(null);
  const internalNoteModal = ref(false);
  const internalNoteRow = ref(null);
  const taskSearchQuery = ref(null);
  const isDisabled = ref(false);

  /**
   * AB#21240 — the first page fills the window it is in.
   *
   * A fixed size of 10 left roughly half of a 900px window empty and most of a
   * 1400px one unused: the table looked short not because of its layout but
   * because the page had decided not to fetch enough rows. The value is still
   * one of the sizes the select offers, so the number shown and the number sent
   * are the same, and a stored choice from `dashboardRequest` still wins — this
   * only decides the default.
   */
  const DEFAULT_PAGE_SIZES = [10, 20, 50, 100];
  /**
   * AB#21279 follow-up — never fewer than 20 by default. The viewport rule
   * above still asks for more on a tall window; it no longer asks for 10 on a
   * short one, and a stored 10 is lifted too: 10 was the old default, not a
   * choice anybody made, and the ask was "20 by default, and the selector
   * says 20".
   */
  const MIN_DEFAULT_PAGE_SIZE = 20;
  const initialPageSize = Math.max(
    MIN_DEFAULT_PAGE_SIZE,
    pageSizeForViewport(typeof window === 'undefined' ? 0 : window.innerHeight, DEFAULT_PAGE_SIZES)
  );

  let defParams: DashboardRequest = {
    items_for: currentTab.value ?? 'all_items',
    sort_by: 'order_no',
    sort_order: 'desc',
    page: 1,
    search: '',
    per_page: initialPageSize,
    new_dashboard: true,
  };

  const dashboardActionsRef = ref<any>(null);

  /**
   * AB#21240 — what the menu was before the detail pane displaced it, so closing
   * the pane restores the user's choice instead of hard-coding "expanded".
   * `null` means the pane was never opened from this page (a refresh with an
   * order already open), in which case the stored preference decides.
   */
  const sidebarStateBeforePane = ref<boolean | null>(null);

  /** Columns this user has hidden, by label. Persisted across sessions. */
  const storedHiddenColumns = ref<string[]>(readHiddenColumns(localStorage));
  /** AB#23060 — opt-in columns (`defaultHidden`) this user switched on. */
  const shownColumns = ref<string[]>(readHiddenColumns(localStorage, shownColumnsKey()));

  /** AB#21576 — dragged column widths, remembered next to the hidden columns. */
  const { columnWidths, setColumnWidths, resetColumnWidths } = useColumnWidths(COLUMN_PREF_KEY);
  const canResetColumns = computed(
    () =>
      shownColumns.value.length > 0 ||
      hasColumnCustomisation(storedHiddenColumns.value, columnWidths.value)
  );

  /**
   * Apply the user's stored preference when the dashboard first mounts.
   *
   * Deliberately confined to this composable rather than changed in the store:
   * every other page keeps the expanded default it has today, so this PR's
   * effect on the rest of the app is nil.
   */
  const applySidebarPreference = () => {
    // With an order already open the pane owns the space; leave the menu
    // collapsed and let `onDetailsClose` restore the preference.
    if (openDetails.value) {
      sidebarOpen.value = false;
      return;
    }
    sidebarOpen.value = sidebarShouldBeOpen(localStorage);
  };

  const defParamsString = JSON.stringify({ dashboardRequest: defParams });

  /**
   * Bug: the dashboard showing exactly one order, forever.
   *
   * `order_id` is a ONE-SHOT deep-link filter. The "view this order" email link arrives as
   * ?order_id=…&order_item_id=…, and the block below copies it into `current_filters` so the
   * first fetch lands on that order. The backend turns it into a hard
   * `where('order_id', …)` in AdminDashboardController::getItemsFor.
   *
   * The URL is cleared straight after (step 3), but `current_filters` is persisted to
   * localStorage['dashboardRequest'] and every later path spreads it forward, so the key
   * survives reloads, tab switches and logouts with nothing left to explain it. The list,
   * the tab counts and Export Order Data all silently collapse to that single order.
   *
   * So: strip it from anything read back out of localStorage — the URL block below is the
   * only thing allowed to set it — and consume it after the first fetch. Stripping on read
   * also heals the browsers that already have a stale id saved.
   */
  const stripDeepLinkFilter = (filters: DashboardRequest): DashboardRequest => {
    if (!filters || filters.order_id === undefined) return filters;
    const { order_id: _oneShot, ...rest } = filters;
    return rest as DashboardRequest;
  };

  const readSavedFilters = (): DashboardRequest => {
    const saved = stripDeepLinkFilter(
      (
        JSON.parse(localStorage.getItem('dashboardRequest') ?? defParamsString) as Record<
          string,
          DashboardRequest
        >
      ).dashboardRequest
    );
    // See MIN_DEFAULT_PAGE_SIZE: a saved page size below the floor is the old
    // default, not a preference, and comes up to the floor.
    if (typeof saved.per_page === 'number' && saved.per_page < MIN_DEFAULT_PAGE_SIZE) {
      saved.per_page = MIN_DEFAULT_PAGE_SIZE;
    }
    return saved;
  };

  const currentDashboardModel: Record<string, DashboardRequest> = {
    dashboardRequest: readSavedFilters(),
  };
  // AB#21279 — the restore target is the active tab. readTabs() migrates the
  // single `currentRow` + `openDetails` pair the pane saved before tabs.
  const savedRowIds = ref<{ order_id?: number; id?: number } | null>(
    (() => {
      const active = activeOrderTab(orderTabs.value);
      return active ? { order_id: active.orderId, id: active.itemId } : null;
    })()
  );
  const currentActiveRow = ref<DashboardItem | undefined | null>(null);
  const openDetails = ref<boolean>(orderTabs.value.tabs.length > 0);
  const fetchOrder = ref<boolean>(true);
  const current_filters = ref<DashboardRequest>(currentDashboardModel.dashboardRequest);
  // near the top of useDashboard()
  const { user: currentUser } = storeToRefs(store);
  // Check for order_no in URL query parameters
  const route = useRoute();
  const router = useRouter();
  let popperInstance: any = null;

  // 1) Read URL params once
  const urlOrderNo = route.query.order_no ?? null;
  const urlOrderId = route.query.order_id ?? null;
  const urlOrderItemId = route.query.order_item_id ?? null;
  // AB#21732 — a Needs Attention button says what to do on arrival
  // (na_action) and, for a date action, which date (na_date). It used to be
  // sent and never read, so every queue button only opened the order.
  const urlAction = route.query.na_action ?? null;
  const urlActionDate = route.query.na_date ?? null;
  // AB#21966 — a link from the team chat email or bell opens the order on its Team chat.
  const urlInnerTab =
    route.query.tab === 'team_chat' && can('view-order-team-chat') ? 'team_chat' : null;
  const pendingAction = ref<(ElsaActionRequest & { orderItemId: number }) | null>(null);
  const clearPendingAction = (): void => {
    pendingAction.value = null;
  };
  // AB#21615 — a FILTERED deep link (e.g. the Needs Attention team overview:
  // ?order_admin=<id>&filter_by=needs_attention). Applies quick filters and shows
  // the listing, rather than opening one order.
  const urlOrderAdmin = route.query.order_admin ?? null;
  const urlFilterBy = route.query.filter_by ?? null;

  // 2) If deep-link params exist, configure filters + saved row BEFORE creating the query
  if (urlOrderId && urlOrderItemId) {
    // Force filters to search by this order_no on first load
    current_filters.value = {
      ...current_filters.value,
      items_for: currentTab.value ?? 'all_items',
      sort_by: 'order_no',
      sort_order: 'desc',
      page: 1,
      // An order without a number yet is found by order_id alone (AB#21886).
      search: urlOrderNo ? String(urlOrderNo) : '',
      new_dashboard: true,
      order_id: Number(urlOrderId),
    };

    // Reset quick filters so they don't conflict
    dashboardQuickFilters.value = {};

    if (isOrderActionCode(urlAction) && urlAction !== 'view_order') {
      pendingAction.value = {
        orderItemId: Number(urlOrderItemId),
        code: urlAction,
        date: typeof urlActionDate === 'string' ? urlActionDate : null,
      };
    }

    // Prepare detail pane state so that once data comes, we can open that row
    savedRowIds.value = {
      order_id: Number(urlOrderId),
      id: Number(urlOrderItemId),
    };
    openDetails.value = true;
    localStorage.setItem('currentRow', JSON.stringify(savedRowIds.value));
    localStorage.setItem('openDetails', JSON.stringify(true));
    localStorage.setItem('activeDetailsTab', urlInnerTab ?? deskDefaultTab());
    // AB#21279 — a deep link is a row click that arrived by URL: it gets a tab.
    {
      const opened = openTab(
        orderTabs.value,
        {
          orderId: Number(urlOrderId),
          itemId: Number(urlOrderItemId),
          label: urlOrderNo ? `#${urlOrderNo}` : '',
          merchant: '',
          innerTab: urlInnerTab ?? deskDefaultTab(),
        },
        Date.now()
      );
      orderTabs.value = opened.state;
    }

    // 3) Clear the URL so refresh/back doesn't re-trigger this deep-link
    router.replace({ query: {} });
  } else if (urlOrderAdmin || urlFilterBy) {
    // AB#21615 — a filtered deep link: pre-apply the quick filters and show the
    // listing. Same shape the filter UI builds (see DashboardFilters
    // buildQuickFiltersObj): filter_by is a list of chip values; order_admins is
    // { comparison, values }.
    current_filters.value = {
      ...current_filters.value,
      items_for: currentTab.value ?? 'all_items',
      page: 1,
      search: '',
      new_dashboard: true,
    };

    const qf: Record<string, any> = {};
    if (urlFilterBy) {
      qf.filter_by = Array.isArray(urlFilterBy) ? urlFilterBy.map(String) : [String(urlFilterBy)];
    }
    if (urlOrderAdmin) {
      const ids = (Array.isArray(urlOrderAdmin) ? urlOrderAdmin : [urlOrderAdmin])
        .map(Number)
        .filter((n) => !Number.isNaN(n));
      if (ids.length > 0) {
        qf.order_admins = { comparison: 'equal', values: ids };
      }
    }
    dashboardQuickFilters.value = qf;

    // Persist to the SAME key DashboardFilters hydrates from on mount
    // (AB#21615 follow-up). The panel seeds its own selection state — the chips
    // and the Order Admins custom filter — from localStorage['dashboardQuickFilters'],
    // not from this store ref. Without this write it mounts with an empty
    // selection, its watcher rebuilds an empty quick_filters object and emits
    // updateFilter, and that overwrites the store ref we just set back to {} —
    // so the deep link showed the whole, unfiltered list. Writing qf here lets
    // the panel restore 'needs_attention' and order_admins, rebuild the same
    // object, and leave the store intact. Wrapped: storage can throw (private
    // mode, quota), and a filtered first paint via the store ref matters more
    // than persistence.
    try {
      localStorage.setItem('dashboardQuickFilters', JSON.stringify(qf));
    } catch {
      /* non-fatal: the store ref above still drives the first fetch */
    }

    // Clear the URL so refresh/back doesn't re-trigger this deep-link.
    router.replace({ query: {} });
  }

  // 4) Normal query ? no router calls or URL logic inside
  const { data, error, isFetching, isPending } = useVueQuery<OrderPage, Error>(
    'orders',
    async () => {
      const params: DashboardRequest = {
        ...defParams,
        ...current_filters.value,
        page: current_filters.value.page,
      };

      await store.getDashboardData(params, dashboardQuickFilters.value);

      // One-shot: the deep link has done its job, so the next refetch (a sort, a page change,
      // a save) is not still pinned to that one order.
      current_filters.value = stripDeepLinkFilter(current_filters.value);

      const currentData = dashboardData.value?.items;

      // Restore active row from saved IDs if available.
      //
      // Bug 20628 (Zendesk #8494) — this block runs inside the orders query fn, so it re-executes on
      // EVERY orders refetch, including the one `fetchOnSave` triggers via `invalidateQueries(['orders'])`
      // after a save. Because `savedRowIds` was seeded once from `localStorage['currentRow']` and never
      // consumed, a save could re-target `currentRow` / `selectedOrderDetails` to whatever order was last
      // handled on that machine — which remounted the open Price Breakdown modal onto that other order
      // (and, on `master`, left it there). Three guards keep it to its intended job, "reopen the pane I
      // had open when the page loaded":
      //   1. never re-target while an order-scoped modal is open,
      //   2. consume `savedRowIds` once used, so a later refetch cannot resurrect it,
      //   3. `handleCellClick` / `openDashboardActions` clear it as soon as the user picks a row
      //      themselves — at that point the restore intent is already obsolete.
      if (
        savedRowIds.value &&
        savedRowIds.value.order_id &&
        savedRowIds.value.id &&
        fetchOrder.value &&
        openDetails.value &&
        !priceBreakdownModal.value &&
        currentData?.data
      ) {
        const restoreIds = savedRowIds.value;
        // Consume up-front: `handleCellClick` below awaits, and any refetch racing that await must not
        // see the ids as still pending.
        savedRowIds.value = null;

        const savedRow = currentData.data.find(
          (row: DashboardItem) => row.id === restoreIds.id && row.order?.id === restoreIds.order_id
        );

        if (savedRow) {
          currentActiveRow.value = savedRow;
          await handleCellClick(
            {
              row: savedRow,
              column: {
                key: 'id',
                label: 'id',
              },
            },
            { fromRestore: true }
          );
        } else {
          // AB#21279 — the row is not on this page; the tab knows its ids, and
          // showTab fetches the order and builds the row out of it (see
          // rowForRestoredPane) the same way this branch used to inline.
          try {
            await showTab(tabKey(restoreIds.order_id, restoreIds.id));
            if (currentRow.value) currentActiveRow.value = currentRow.value;
          } catch (error) {
            logError('Failed to restore order details:', error);
          }
        }
      }

      return {
        items: currentData?.data ?? [],
        total: currentData?.total ?? 0,
      };
    },
    {
      queryKey: [
        'orders',
        currentTab,
        dashboardQuickFilters,
        defParams.sort_by,
        defParams.sort_order,
        defParams.search,
        current_filters.value.page,
      ],
      refetchOnWindowFocus: false,
    }
  );

  const tabs = [
    { id: 'timeline', label: 'Timeline' },
    { id: 'detail', label: 'Detail' },
    { id: 'files', label: 'Files' },
    { id: 'activity_logs', label: 'Activity Logs' },
    // AB#22077 — the audit log's entries for this order (view-record-activity).
    { id: 'audit', label: 'Activity' },
    // AB#21202 — Ask ELSA. A tab rather than a button in the action row: the
    // order detail is already the right-hand pane, so an overlay would cover
    // the timeline every claim in the analysis cites, and a button that only
    // switches to a tab is a duplicate control in a row AB#21204 just cut from
    // twenty-one to nine.
    //
    // `variant` and `separated` make it look like what it is. Shipped first as
    // a plain tab, which was wrong: identical shape to the four beside it meant
    // it read as a fifth view of the order rather than as someone you ask, and
    // when it was not selected nothing distinguished it at all.
    // AB#21966 — Team chat: the order's internal chat (view-order-team-chat).
    { id: 'team_chat', label: 'Team chat', variant: 'team' },
    { id: 'ask_elsa', label: 'Ask ELSA', variant: 'agent', separated: true },
  ];
  const filteredOrderDetailTabs = computed(() => {
    return tabs.filter((tab) => {
      if (tab.id === 'files') return can('sharepoint-files');
      if (tab.id === 'activity_logs') return can('view-activity-logs');
      if (tab.id === 'audit') return canViewRecordActivity();
      if (tab.id === 'team_chat') return can('view-order-team-chat');
      // AB#21422 — Ask ELSA is for the internal desk. Not a permission (its
      // route mapping would have to exist in the production database in the
      // same release — AB#21176) but a role rule, the same one the ELSA
      // routes enforce with a 403: the backend says so on authuser, and until
      // it does the roles are read the way the backend reads them.
      if (tab.id === 'ask_elsa') return elsaAllowed(store.user?.roles, store.features);
      return true;
    });
  });

  /**
   * The inner tab a freshly opened order lands on.
   *
   * Ask ELSA is the desk's default now that every order carries a reading; a
   * user without ELSA access (merchants, restricted roles) still opens on the
   * timeline. This only sets where a NEW order opens — a tab the user has
   * explicitly switched is remembered per order (AB#21279) and wins on restore.
   * It resolves to 'ask_elsa' only when the ELSA tab is actually available, so
   * the pane can never open on a tab that was filtered out.
   */
  function deskDefaultTab(): string {
    return elsaAllowed(store.user?.roles, store.features) ? 'ask_elsa' : 'timeline';
  }
  const canAssignOrderAdmin = computed(() => can('assign-order-administrator'));
  const currentRow = ref<DashboardItem | null>(null);
  const previousSelectedRow = ref<DashboardItem | null>(null);

  const selectedRow: ComputedRef<DashboardItem | null> = computed(() => {
    if (
      currentActiveRow.value &&
      fetchOrder.value &&
      datatable.value &&
      !previousSelectedRow.value &&
      openDetails.value
    ) {
      fetchOrder.value = false;
      scrollToCurrentRow();
      return currentActiveRow.value;
    } else if (
      openDetails.value === false &&
      datatable.value &&
      !currentRow.value &&
      currentActiveRow.value
    ) {
      previousSelectedRow.value = JSON.parse(JSON.stringify(currentActiveRow.value));
      currentActiveRow.value = null;
      scrollToCurrentRow();
      return previousSelectedRow.value;
    } else if (currentRow.value) {
      return currentRow.value;
    } else if (previousSelectedRow.value) {
      return previousSelectedRow.value;
    } else {
      return null;
    }
  });

  const canViewShippingAddress = computed(() => can('order-listing-shipping-address-column'));
  const canViewMerchant = computed(() => can('order-listing-merchant-column'));
  const canViewPaymentStatus = computed(() => can('view-payment-status'));
  const canViewFactory = computed(() => can('order-listing-factory-column'));
  // AB#23060 — internal roles only; the API leaves the dates out for anyone else.
  const canViewFactoryDeadline = computed(() => can('view-factory-deadline'));

  const filteredModeTabs = computed(() => {
    return modeTabs.value.filter((tab) => {
      if (tab.id === 'calendar') {
        return can('can-see-calendar');
      }
      if (tab.id === 'my-tasks') {
        return can('view-my-tasks');
      }
      return true;
    });
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500';
      case 'processing':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-500';
      case 'cancelled':
        return 'bg-red-500';
      case 'delayed':
        return 'bg-orange-500';
      default:
        return 'bg-gray-500';
    }
  };
  // AB#19827 (Zendesk#7813) - My Orders tab only: when a customer rejects a sample the order
  // item stays at "submitted_for_customer_review" while the individual factory product becomes
  // "customer_rejected". Detect that so the order admin can spot it at a glance. Display-only.
  const REJECTED_SAMPLE_STATUS = 'customer_rejected';
  const CUSTOMER_REVIEW_STATUS = 'submitted_for_customer_review';
  const isMyOrdersRejected = (row: DashboardItem): boolean => {
    if (currentTab.value !== 'my_orders') return false;
    if (row?.status !== CUSTOMER_REVIEW_STATUS) return false;
    let fps: any = row?.factory_products;
    if (typeof fps === 'string') {
      try {
        fps = JSON.parse(fps);
      } catch (e) {
        fps = [];
      }
    }
    return Array.isArray(fps) && fps.some((fp: any) => fp?.status === REJECTED_SAMPLE_STATUS);
  };
  const orderOptions = computed(() =>
    orderList.value.map((order) => ({
      ...order,
      label: order.customer_reference_no
        ? `${order.order_no} (${order.customer_reference_no})`
        : order.order_no,
    }))
  );
  const handlePriceBreakDownModalCloseEvent = async (fetch_order_items = false) => {
    priceBreakdownModal.value = false;
    // `handleOrderUpdated` already runs `getOrderDetail` + `fetchOnSave(true)` ? do not call `fetchOnSave` twice.
    if (fetch_order_items) {
      await handleOrderUpdated();
    }
  };
  const onOrderSearch = async (query: string, loading: (b: boolean) => void) => {
    loading(true);
    await dashboardStore.searchOrders(query);
    loading(false);
  };

  const onModeTabChange = (tabId: string) => {
    dashboardStore.setCurrentMode(tabId);

    if (tabId === 'calendar' && !dashboardStore.calendarInitialized) {
      dashboardStore.initializeCalendar();
    }
  };

  const handleViewChanged = (payload: {
    view: string;
    selected_date: string;
    selected_holiday_country_code: string;
  }) => {
    dashboardStore.setCurrentView(payload.view);
    dashboardStore.setSelectedDate(new Date(payload.selected_date));
    dashboardStore.setSelectedHolidayCountryCode(payload.selected_holiday_country_code);
  };

  const onCalendarFilterChange = (filters: any) => {
    if (currentMode.value === 'calendar') {
      if (
        filters &&
        typeof filters === 'object' &&
        ('items_for' in filters || 'quick_filters' in filters)
      ) {
        if (!filters.quick_filters || Object.keys(filters.quick_filters).length === 0) {
          dashboardStore.setCalendarFilters(null);
        } else {
          dashboardStore.setCalendarFilters(filters);
        }
        if (filters.items_for === 'my_assigned_orders') {
          dashboardStore.setCalendarFilters({
            ...filters,
            my_assigned_orders: true,
          });
        }
      } else {
        dashboardStore.setCalendarFilters(null);
      }
    } else {
      selectedFilter.value = typeof filters === 'string' ? filters : null;
    }
  };

  const factoryModalClose = () => {
    assignFactoryModal.value = false;
    selectedOrderItem.value = null;
  };

  const tableData = computed(() => {
    return data.value?.items ?? [];
  });

  const totalItems = computed(() => {
    return data.value?.total ?? 0;
  });

  const fetchRecords = async (data: any): Promise<void> => {
    defParams = {
      items_for: currentTab.value ?? 'all_items',
      sort_by: data?.sortColumn ?? 'order_no',
      sort_order: data?.sortOrder ?? data?.sort_order,
      page: data?.page ?? 1,
      per_page: data?.pageSize ?? data?.per_page,
      search: data?.searchQuery ?? '',
      new_dashboard: true,
    };

    current_filters.value = {
      ...current_filters.value,
      ...defParams,
    };

    localStorage.setItem(
      'dashboardRequest',
      JSON.stringify({ dashboardRequest: current_filters.value })
    );

    await queryClient.invalidateQueries({ queryKey: ['orders'] });
  };

  const onTabChange = async (tab: string): Promise<void> => {
    if (tab === 'calendar') {
      currentTab.value = tab;
    } else {
      currentTab.value = tab;
      defParams = {
        ...defParams,
        items_for: currentTab.value,
        sort_by: 'order_no',
        sort_order: 'desc',
        page: 1,
      };
      current_filters.value = {
        ...readSavedFilters(),
        ...defParams,
      };
    }
  };

  const fetchOnSave = async (fetch: boolean, skipOrderDetailRefetch = false) => {
    if (!fetch) return;

    current_filters.value = readSavedFilters();

    const row = currentRow.value;
    let detailItemFromRefetch: OrderDetailItem | undefined;
    let detailForRow: OrderDetail | undefined;

    if (row && !skipOrderDetailRefetch) {
      try {
        const orderData = await store.getOrderDetail(row.order.id, row.id);
        detailForRow = orderData;
        selectedOrderDetails.value = orderData;
        detailItemFromRefetch = orderData?.items?.find((i) => i.id === row.id);
        if (detailItemFromRefetch && currentRow.value) {
          const refetchedPriceInfo = detailItemFromRefetch.price_info as unknown as
            DashboardItem['price_info'] | undefined;
          const refetchedTotalPrice = (
            detailItemFromRefetch.price_info as { total_price?: number } | undefined
          )?.total_price;
          currentRow.value = {
            ...currentRow.value,
            price_info: refetchedPriceInfo ?? currentRow.value.price_info,
            currency: detailItemFromRefetch.currency ?? currentRow.value.currency,
            billing_company_id:
              detailItemFromRefetch.billing_company_id ?? currentRow.value.billing_company_id,
            factory_products:
              detailItemFromRefetch.factory_products ?? currentRow.value.factory_products,
            item_invoices:
              (detailItemFromRefetch as any).item_invoices ??
              (detailItemFromRefetch as any).itemInvoices ??
              currentRow.value.item_invoices,
            invoice_composite_status:
              detailItemFromRefetch.invoice_composite_status ??
              currentRow.value.invoice_composite_status,
            order_item_total_price:
              Number(refetchedTotalPrice) || currentRow.value.order_item_total_price,
          };
        }
      } catch (error) {
        logError('Error refetching order details after price breakdown save:', error);
      }
    }


    await queryClient.invalidateQueries({ queryKey: ['orders'] });

    // Bug 20628 (Zendesk #8494) — the refetch above re-runs the orders query fn, which may re-target
    // `selectedOrderDetails` at another order. `currentRow` is healed below, but nothing restored the
    // detail payload, so the pane and the `:key`-ed Price Breakdown modal stayed on the wrong order.
    // Re-assert the payload we already fetched for `row` — no extra request.
    if (detailForRow && selectedOrderDetails.value?.id !== detailForRow.id) {
      selectedOrderDetails.value = detailForRow;
      setPageTitle(`#${detailForRow.order_no}`);
    }

    if (row && tableData.value && Array.isArray(tableData.value)) {
      const updated = tableData.value.find((r) => r.id === row.id);
      if (updated) {
        const refetchedPriceInfo = detailItemFromRefetch?.price_info as unknown as
          DashboardItem['price_info'] | undefined;
        const refetchedTotalPrice = (
          detailItemFromRefetch?.price_info as { total_price?: number } | undefined
        )?.total_price;
        currentRow.value = {
          ...updated,
          price_info: refetchedPriceInfo ?? updated.price_info,
          currency: detailItemFromRefetch?.currency ?? updated.currency,
          billing_company_id:
            detailItemFromRefetch?.billing_company_id ?? updated.billing_company_id,
          factory_products: detailItemFromRefetch?.factory_products ?? updated.factory_products,
          item_invoices:
            (detailItemFromRefetch as any)?.item_invoices ??
            (detailItemFromRefetch as any)?.itemInvoices ??
            updated.item_invoices,
          invoice_composite_status:
            detailItemFromRefetch?.invoice_composite_status ?? updated.invoice_composite_status,
          order_item_total_price:
            refetchedTotalPrice != null
              ? Number(refetchedTotalPrice)
              : updated.order_item_total_price,
        };
        localStorage.setItem(
          'currentRow',
          JSON.stringify({ order_id: currentRow.value.order.id, id: currentRow.value.id })
        );
      }
    }

    scrollToCurrentRow();
    await invalidateOrderActivityLogQueries();
  };

  const loginCustomer = async (customer_id: number): Promise<void> => {
    loading.value = true;
    await http
      .get(`/customer/information/${customer_id}`)
      .then((res) => {
        let token = res.data.access_token;
        if (res.data.user.company_url) {
          const url = res.data.user.company_url + '/#/';
          // Tell the builder who is impersonating so its banner can name the
          // admin. If this session is itself an impersonated one, credit the
          // original admin from the impersonation context.
          const mainStore = useMainStore();
          const impBy = getImpersonation()?.by ?? {
            name: mainStore.user?.name ?? '',
            email: mainStore.user?.email ?? '',
            role: mainStore.role?.name ?? '',
          };
          const impParams =
            `&imp_by_name=${encodeURIComponent(impBy.name || '')}` +
            `&imp_by_email=${encodeURIComponent(impBy.email || '')}` +
            `&imp_by_role=${encodeURIComponent(impBy.role || '')}`;
          window.open(`${url}admin/login?token=${token}${impParams}`);
        }
      })
      .catch((err) => {
        logError(err);
      });
    loading.value = false;
  };

  const getUrgencyClass = (urgency: Urgency, id: number): string => {
    switch (urgency) {
      case 'VIP':
        return 'bg-green-300 !text-ink-soft !p-0 ';
      case 'Urgent':
        return 'order_cancel !text-ink-soft !p-0';
      case 'Extra Attention':
        return 'bg-blue-300 !text-ink-soft !p-0';
      default:
        return `!p-0 ${id && selectedRow.value && selectedRow.value.id === id ? 'bg-surface-3' : ''}`;
    }
  };

  const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

  const getDaySuffix = (day: number): string => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  const parseDashboardCalendarDate = (dateValue: string): Date | null => {
    const trimmed = String(dateValue ?? '').trim();
    if (!trimmed) return null;

    // Keep date-only strings stable across different local timezones.
    if (DATE_ONLY_REGEX.test(trimmed)) {
      const [year, month, day] = trimmed.split('-').map(Number);
      const localDate = new Date(year, month - 1, day);
      return Number.isNaN(localDate.getTime()) ? null : localDate;
    }

    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDashboardEstShippingDate = (dateValue: string, shortFormat: boolean): string => {
    const date = parseDashboardCalendarDate(dateValue);
    if (!date) return dateValue;

    const day = date.getDate();
    if (shortFormat) {
      const shortDay = String(day).padStart(2, '0');
      const shortMonth = date.toLocaleString('en-US', { month: 'short' });
      const shortYear = String(date.getFullYear()).slice(-2);
      return `${shortDay} ${shortMonth} ${shortYear}`;
    }

    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    return `${day}${getDaySuffix(day)} ${month} ${year}`;
  };

  /**
   * AB#23060 — the Factory deadline cell: the date, whose it is ("set by us" or the
   * reporting factory), and a red/amber tone when it has passed or is days away on an
   * order the factory still holds.
   */
  const factoryDeadlineCell = (row: DashboardItem) => {
    const deadline = factoryDeadline(row);
    if (!deadline.date) return h('span', { class: 'text-ink-mute' }, '—');
    const tone = factoryDeadlineTone(deadline.date, row.status);
    const cls =
      tone === 'overdue'
        ? 'font-semibold text-[var(--danger)]'
        : tone === 'soon'
          ? 'font-semibold text-[var(--warning)]'
          : '';
    return h(
      'span',
      {
        class: 'inline-flex flex-col leading-tight whitespace-nowrap',
        title: factoryDeadlineTitle(deadline, formatDate(deadline.date), tone),
        'data-test': 'factory-deadline-cell',
        'data-factory-deadline-tone': tone ?? 'none',
        'data-factory-deadline-source': deadline.source ?? 'none',
      },
      [
        h('span', { class: cls }, formatShortDate(deadline.date)),
        h('span', { class: 'text-[11px] text-ink-mute' }, deadline.sourceLabel),
      ]
    );
  };

  const visibleColumns = computed(() => {
    const baseColumns = [
      {
        key: currentTab.value === 'quote_requested_items' ? 'order_no' : 'quote_no',
        label: currentTab.value === 'quote_requested_items' ? `Quote No` : `Order No`,
        sortable: true,
        sortKey: 'order_no',
        // AB#21240 — the column that says WHICH order this row is stays on
        // screen through the sideways scroll, and cannot be hidden.
        sticky: 'left',
        alwaysVisible: true,
        title: 'Order number, with its type, urgency and internal-note markers',
        cellClass: (row: DashboardItem) => {
          if (row.order?.customer_shop_id) {
            // Shop-in-Shop orders get colored
            return '!bg-[#ffb350] !text-white text-center !p-0';
          }
          return currentTab.value !== 'quote_requested_items' && can('show-urgency-filters')
            ? getUrgencyClass((row as DashboardItem).order?.urgency, row?.id)
            : '!p-0 ' +
                (selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '');
        },
        renderer: (val: any, row: DashboardItem) => {
          // Item-level symbol is written by price breakdown updates; order-level is set at order create / some legacy paths.
          const rawSymbol = row.order_type_symbol ?? row.order?.order_type_symbol;
          const urgency = row.order?.urgency;
          const isManualOrder = row.order?.additional_fields?.is_manual_order;
          const orderNumber = row.order
            ? `${isManualOrder ? 'M_#' : '#'}${row.item_no ? row.item_no : row.order?.order_no || ''}`
            : '';
          const cleanedSymbol = rawSymbol?.replace(/i/g, '');
          const allowedSymbols = ['S', 'R'];
          const showSymbol = rawSymbol && allowedSymbols.includes(cleanedSymbol);
          const padding = '0.75rem';

          // AB#20569 — the list said WHAT an order is but never WHICH order it replaces, so
          // the only way to find that out was to open the detail pane one order at a time.
          // The link is already on the row (originalOrder is eager loaded); this surfaces it
          // as a tooltip so the column stays as narrow as it is.
          //
          // Reorders were invisible entirely: 'REORDER' is not in allowedSymbols and survives
          // the /i/ strip unchanged, so nothing rendered at all. They get their own marker,
          // deliberately NOT red and NOT an "R" — a reorder is not a failure and must not read
          // like a remake at a glance.
          const originalOrder = row.order?.original_order;
          const originalOrderNo = originalOrder?.order_no ?? originalOrder?.id ?? null;
          const isRemake = rawSymbol === 'Ri' || rawSymbol === 'R';
          const isReorder = rawSymbol === 'REORDER';

          const linkTooltip = isRemake
            ? originalOrderNo
              ? `Remake of order #${originalOrderNo}`
              : 'Remake — the original order is not recorded'
            : isReorder
              ? originalOrderNo
                ? `Reorder of order #${originalOrderNo}`
                : 'Reorder — the original order is not recorded'
              : null;

          /** The bare letter, wrapped in a tooltip when there is something to explain. */
          const symbolBadge = (label: string, color: string) => {
            const badge = h(
              'span',
              {
                style: {
                  color,
                  fontWeight: 'bold',
                  marginLeft: '4px',
                  fontSize: '1rem',
                },
              },
              label
            );

            if (!linkTooltip) return badge;

            return h(Tooltip, { placement: 'top', content: linkTooltip }, { trigger: () => badge });
          };

          const children = [
            orderNumber,
            showSymbol && symbolBadge(cleanedSymbol, 'red'),
            isReorder && symbolBadge('RE', '#2563eb'),
          ];
          if (row.internal_comment) {
            children.push(
              h(
                Tooltip,
                {
                  placement: 'top',
                  content: JSON.parse(row.internal_comment).comment,
                  multiline: true,
                },
                {
                  trigger: () =>
                    h(
                      'button',
                      {
                        class: 'ml-4 cursor-pointer align-middle',
                        style:
                          'display:inline-flex;vertical-align:middle;background:none;border:none;padding:0;',
                        onClick: (e) => {
                          e.stopPropagation();
                          openInternalNoteModal(row);
                        },
                        title: 'View/Add Internal Note',
                      },
                      [
                        h(ChatBubbleBottomCenterTextIcon, {
                          class: 'size-5 text-[#2600D8]',
                        }),
                      ]
                    ),
                }
              )
            );
          }

          // Show on-hold icon with tooltip containing latest reason (if order is on_hold)
          if (row.status === 'on_hold') {
            let holdReasonText = 'N/A';

            try {
              const raw = row.on_hold_reason;
              const parsed = typeof raw === 'string' && raw.trim() ? JSON.parse(raw) : raw;

              if (Array.isArray(parsed) && parsed.length > 0) {
                const last = parsed[parsed.length - 1];

                // New format: { status, reason, timestamp, user_id }
                if (last && typeof last === 'object') {
                  holdReasonText = last.reason || 'N/A';
                }
              }
            } catch (err) {
              holdReasonText = row?.on_hold_reason || 'N/A';
            }

            children.push(
              h(
                Tooltip,
                {
                  placement: 'top',
                  contentClass: 'whitespace-normal max-w-sm !p-4',
                },
                {
                  trigger: () =>
                    h('img', {
                      src: orderHoldIcon,
                      class: 'ml-2 flex-shrink-0', // Added flex-shrink-0
                      style: {
                        width: '20px', // Explicit width
                        height: '20px', // Explicit height
                        minWidth: '20px', // Prevent shrinking
                        minHeight: '20px', // Prevent shrinking
                        objectFit: 'contain', // Maintain aspect ratio
                      },
                    }),
                  default: () =>
                    h('div', { class: 'flex flex-col gap-2 max-w-sm w-full z-[10000]' }, [
                      h(
                        'div',
                        {
                          class:
                            'font-bold text-[18px] text-ink pb-2 z-[10000] border-b border-line mb-2 mt-2 break-words',
                        },
                        'Order on hold'
                      ),
                      h('div', { class: 'text-[14px] leading-relaxed max-w-sm z-[10000]' }, [
                        h(
                          'div',
                          { class: 'font-semibold text-ink-soft mb-2 z-[10000]' },
                          'Reasoning:'
                        ),
                        h(
                          'div',
                          {
                            class:
                              'text-ink-soft pl-2 whitespace-normal break-words max-w-full z-[10000]',
                          },
                          holdReasonText
                        ),
                      ]),
                    ]),
                }
              )
            );
          }

          const cellChilds = [
            h(
              'span',
              {
                class: 'text-sm min-w-0 flex-1',
                style: {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                },
              },
              children
            ),
            // AB#21242 — the Reorder pill used to sit on a second line, which
            // made those rows 62px against 44px for every other row. Same pill,
            // same words, beside the order number instead of under it.
            // AB#21540 — a soft chip, not a solid accent: it says what the order
            // is, it is not something to click, and it must not outrank the
            // primary button on every reorder row.
            row.reorder_order_ids &&
              h(
                'span',
                {
                  class:
                    'text-[10px] leading-none bg-primary-light py-1 px-2 rounded-[20px] text-center text-primary-dark font-semibold whitespace-nowrap shrink-0',
                },
                'Reorder'
              ),
          ].filter(Boolean);
          const cellContent = h(
            'div',
            {
              class: 'w-full h-full flex flex-row items-center gap-2',
              style: {
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                padding: padding,
              },
            },
            cellChilds
          );

          if (urgency && can('show-urgency-filters')) {
            return h(
              Tooltip,
              {
                placement: 'right',
                content: `${urgency ? urgency.replace(/_/g, ' ') : 'normal'} Order`,
                widthClass: 'w-full',
              },
              {
                trigger: () => cellContent,
              }
            );
          } else {
            return cellContent;
          }
        },
      },
      {
        key: 'customer',
        label: openDetails.value ? 'By' : 'Customer',
        title: 'Customer, and a shortcut to log in as them',
        // AB#21243 — at the shared 200px cap most names truncated to a first
        // word. This is the column you read to know whose order this is.
        widthClass: 'max-w-[280px]',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          const { customer_name, customer_email } = row.order;

          let mainContent;
          const customerName = cleanName(customer_name);
          if (openDetails.value || customerName.length > 30) {
            // Show initials with tooltip
            let displayVal = '';
            let tooltipVal = '';

            if (customerName?.trim()?.length) {
              tooltipVal = customerName;
              // Get initials (first letter of each word, max 2)
              const initials = customerName
                .split(' ')
                .map((n) => n[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();
              displayVal = `${initials}..`;
            } else if (customer_email?.trim()?.length) {
              tooltipVal = customer_email;
              displayVal = `${customer_email.slice(0, 2).toUpperCase()}...`;
            }

            mainContent = h(
              Tooltip,
              {
                placement: 'top',
                content: tooltipVal,
              },
              {
                trigger: () =>
                  h(
                    'span',
                    { class: 'whitespace-nowrap font-semibold min-w-0 truncate flex-1' },
                    displayVal
                  ),
              }
            );
          } else {
            // Show full name or email
            mainContent = h(
              'span',
              { class: 'whitespace-nowrap font-semibold min-w-0 truncate flex-1' },
              customerName?.trim()?.length ? customerName : customer_email
            );
          }

          // "Login as Customer" button with icon and tooltip
          const loginButton = h(
            Tooltip,
            {
              placement: 'right',
              content: 'Login as Customer',
            },
            {
              trigger: () =>
                h(
                  'button',
                  {
                    // AB#21242 — this was a solid teal block on every row, which
                    // made the loudest thing in the table an action almost nobody
                    // takes. Quiet by default, teal on hover, so it is still
                    // obviously a button and still obviously that button.
                    class:
                      'shrink-0 flex items-center justify-center w-[26px] h-[26px] rounded-[4px] cursor-pointer border-0 bg-transparent text-ink-mute transition-colors hover:bg-[var(--accent)] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)]',
                    'aria-label': 'Login as Customer',
                    onClick: (e: Event) => {
                      // AB#21240 — every other control in a row stops the click
                      // here (the internal-note button, the DQC box, the
                      // administrator select). This one did not, so logging in
                      // as a customer also opened the detail pane behind the new
                      // tab.
                      e.stopPropagation();
                      loginCustomer(row.order.customer_id);
                    },
                  },
                  [
                    // Sized in pixels, not the rem scale: this app's 12.8px
                    // root font makes `w-4` render at 12.8px, not 16px.
                    h(KeyIcon, {
                      class: 'w-[16px] h-[16px]',
                    }),
                  ]
                ),
            }
          );

          // AB#21242 — `justify-between` did nothing here because the wrapper was
          // an inline span that shrank to its content, so the key sat immediately
          // after the name and landed at a different x on every row. `w-full`
          // gives it the cell to spread across, which puts every key on one line
          // down the right edge of the column.
          return h('span', { class: 'flex w-full gap-2 items-center justify-between' }, [
            mainContent,
            // The shared Tooltip's root is `relative inline-block w-full flex`.
            // As a direct flex child it stretched to fill the space left over,
            // and its trigger sat at ITS left edge — so `justify-between` moved a
            // full-width box to the right and the key stayed where it was, with
            // dead space after it. Caging it in a shrink-to-fit span makes the
            // Tooltip's own `w-full` resolve against the button, not the column.
            h('span', { class: 'shrink-0 flex items-center' }, [loginButton]),
          ]);
        },
      },
      {
        key: 'date',
        // AB#21297 — "Date" said nothing about which date, on a row that also
        // carries an estimated shipping date and an idle time. It is the day
        // the order was created, so the header says so. (The sort key stays
        // `status_updated_at`: the API maps that name to orders.created_at.)
        label: 'Created',
        title: 'Date the order was created',
        sortable: true,
        sortKey: 'status_updated_at',
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          const dateValue = row.order.created_at ?? Date.now().toString();
          // AB#21242 — the short form everywhere, not only beside an open pane.
          // The renderer already knew "14th September 2026" was unusable at half
          // width; it is no more useful at full width, where it was the single
          // biggest contributor to an 85px row. The full date is on hover.
          return h(
            'span',
            { class: 'whitespace-nowrap', title: formatDate(dateValue) },
            formatShortDate(dateValue)
          );
        },
      },
      {
        key: 'est_shipping_date',
        // AB#21576 — shortened; the tooltip keeps the full name.
        label: 'Est. Shipping',
        title: 'Estimated shipping date — empty until a factory is assigned',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          // AB#21245 — an em dash reads as "nothing here"; "N/A" reads as a value.
          if (!row.est_shipping_date) return h('span', { class: 'text-ink-mute' }, '—');
          const dateVal = Array.isArray(row.est_shipping_date)
            ? row.est_shipping_date[row.est_shipping_date.length - 1]
            : row.est_shipping_date;
          // AB#21242 — short form always, for the same reason as the Date column.
          return h(
            'span',
            { class: 'whitespace-nowrap', title: formatDashboardEstShippingDate(dateVal, false) },
            formatDashboardEstShippingDate(dateVal, true)
          );
        },
      },
      {
        // AB#21697 — the customer's in-hand date, beside the promise we made.
        key: 'in_hand_date',
        label: 'In Hand',
        title: 'In-hand date — when the customer needs the goods, set from their own words',
        sortable: true,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          if (!row.in_hand_date) return h('span', { class: 'text-ink-mute' }, '—');
          const tone = inHandTone(row.in_hand_date, row.est_shipping_date);
          const cls =
            tone === 'late'
              ? 'whitespace-nowrap font-semibold text-[var(--danger)]'
              : tone === 'tight'
                ? 'whitespace-nowrap font-semibold text-[var(--warning)]'
                : 'whitespace-nowrap';
          return h(
            'span',
            {
              class: cls,
              title: inHandTitle(
                row.in_hand_date,
                row.est_shipping_date,
                formatDate(row.in_hand_date)
              ),
              'data-in-hand-tone': tone ?? 'none',
            },
            formatShortDate(row.in_hand_date)
          );
        },
      },
      canViewFactoryDeadline.value && {
        // AB#23060 (Zendesk#10462) — opt-in: off until the user ticks it in the column picker.
        key: 'factory_deadline',
        label: 'Factory Deadline',
        title:
          "The deadline we gave the factory, or the factory's own planned dispatch date when we have not set one. Sort ascending to chase the oldest first.",
        defaultHidden: true,
        sortable: true,
        sortKey: 'factory_deadline',
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '';
        },
        renderer: (_val: any, row: DashboardItem) => factoryDeadlineCell(row),
      },
      canViewMerchant.value && {
        key: 'merchant',
        label: 'Merchant',
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '';
        },
        sortable: false,
        renderer: (val: any, row: DashboardItem) =>
          row.billing_company_name
            ? `${row.order.company_name} / ${row.billing_company_name}`
            : row.order.company_name,
      },
      {
        key: 'customer_reference',
        label: 'Customer Reference',
        title: 'The reference the customer gave this order',
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '';
        },
        sortable: false,
        renderer: (val: any, row: DashboardItem) =>
          row.order.customer_reference_no ?? h('span', { class: 'text-ink-mute' }, '—'),
      },
      canViewFactory.value && {
        key: 'factory_name',
        label: 'Factory',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          const factoryName = row?.factory_name ?? '—';
          if (openDetails.value || factoryName.length > 20) {
            // Show first three letters with tooltip
            return h(
              Tooltip,
              {
                placement: 'top',
                content: factoryName,
              },
              {
                trigger: () =>
                  h(
                    'span',
                    { class: 'whitespace-nowrap font-semibold uppercase' },
                    factoryName.slice(0, 3)
                  ),
              }
            );
          }
          // Show full name if details are closed
          return h('span', { class: 'whitespace-nowrap font-semibold' }, factoryName);
        },
      },
      canViewPaymentStatus.value && {
        key: 'invoice_status',
        label: 'Invoice Status',
        title: 'Invoice Status',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id
            ? 'bg-surface-3 capitalize'
            : 'capitalize';
        },
        renderer: (val: any, row: DashboardItem) => {
          const rawComposite = row.invoice_composite_status;
          const composite = rawComposite && String(rawComposite).trim() !== '';
          /*
           * AB#21245 — a bordered pill on every row for a field that is
           * "Not invoiced" on most of them. A pill says "look at me"; this
           * column mostly says "nothing yet". Quiet text, with the pill kept
           * for the states that are genuinely worth a glance.
           */
          const value = composite ? String(rawComposite) : 'not_invoiced';
          const label = composite ? formatInvoiceStatusLabel(value) : 'Not invoiced';
          const LOUD = ['overdue', 'uncollectible', 'uncollectable', 'pending_sync'];
          if (LOUD.includes(value)) {
            return h('span', { class: invoiceCompositeStatusPillClass(value) }, label);
          }
          return h('span', { class: 'whitespace-nowrap text-[#767B83]' }, label);
        },
      },
      {
        key: 'items_total_quantity',
        label: 'Qty',
        title: 'Total quantity across the items on this order',
        sortable: false,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '';
        },
        renderer: (val: any, row: DashboardItem) =>
          row.items_total_quantity ?? h('span', { class: 'text-ink-mute' }, '—'),
      },
      {
        key: 'status',
        label: 'Status',
        sortable: false,
        // AB#21242 — this cell is a centred pill plus a chevron, a rejected
        // badge and the DQC checkbox. Clipping it cuts the pill at both edges.
        noTruncate: true,
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '';
        },
        renderer: (val: any, row: DashboardItem) => {
          const mark_dqc = row.order?.mark_dqc;
          const dqc_user_name = row.order?.dqc_user_name;
          const orderId = row.order?.id;
          const rowId = row?.id;

          const hasReadPermission = can('read-dqc-status');
          const hasWritePermission = can('write-dqc-status');

          const dqcCheckbox = h(
            Tooltip,
            {
              placement: 'right',
              content:
                mark_dqc && dqc_user_name
                  ? `DQC (${dqc_user_name}) approved on ` +
                    formatTimestampWithBrowserTimezone(row?.order?.dqc_marked_at)
                  : mark_dqc
                    ? `DQC approved on ` +
                      formatTimestampWithBrowserTimezone(row?.order?.dqc_marked_at)
                    : hasWritePermission
                      ? 'Click to mark DQC approved'
                      : 'View only (no permission to modify)',
            },
            {
              trigger: () =>
                h(
                  'label',
                  {
                    class: [
                      'flex items-center justify-center text-xs font-medium rounded-xl select-none transition-all duration-150',
                      mark_dqc
                        ? 'border-green-500 text-green-700'
                        : 'border-black-700 text-black-700',
                      hasWritePermission ? 'cursor-pointer' : 'cursor-not-allowed',
                    ],
                    onClick: (e: Event) => e.stopPropagation(), // prevent row click
                  },
                  [
                    h('input', {
                      type: 'checkbox',
                      checked: mark_dqc,
                      disabled: !hasWritePermission || isSidepaneLoading.value,

                      class: [
                        'custom-dqc-checkbox w-4 h-4 rounded-md  border-2 border-black-700 transition-all duration-150 relative',
                        mark_dqc
                          ? 'border-green-500 text-green-700'
                          : 'border-black-700 text-black-700',
                        hasWritePermission ? 'cursor-pointer' : 'cursor-not-allowed',
                      ],
                      onChange: (event: Event) =>
                        hasWritePermission ? toggleDQC(event, orderId, rowId) : null,
                    }),
                  ]
                ),
            }
          );

          const showDropdown =
            row.status === 'order_approve' &&
            !row.factory_id &&
            can('assign-factory-to-shareable-orders');

          const handleClick = async (e) => {
            e.stopPropagation();
            if (!showDropdown) return;

            try {
              const orderData = await store.getOrderDetail(row.order_id, row.id);
              const factory_products = orderData.items?.[0]?.factory_products ?? [];
              const { factory_products: _, ...rest } = row;
              assignFactoryModal.value = true;
              selectedOrderItem.value = {
                ...rest,
                factory_products: factory_products.map((prod) => ({
                  ...prod,
                  selected_factory: prod.selected_factory ?? null,
                })),
              };
            } catch (error) {
              logError('Failed to load order details', error);
            }
          };
          const rowComplaints = getComplaintsArray(row.order);
          const complaintPill = rowComplaints.length
            ? h(ComplaintStatusPill, {
                complaints: rowComplaints,
                clickable: true,
                onPillClick: () => handleComplaintPillClick(row),
              })
            : null;
          const rejectedFlag = isMyOrdersRejected(row);
          const tone = rejectedFlag ? 'bad' : statusTone(row.status);
          return h(
            'div',
            {
              class: 'flex items-center justify-center gap-2',
            },
            [
              h(
                'button',
                {
                  // Not focusable when it does nothing: on most rows this is a
                  // label, and a tab stop that goes nowhere is noise.
                  tabindex: showDropdown ? '0' : '-1',
                  'aria-label': showDropdown
                    ? `Assign a factory to order ${row.order?.order_no ?? ''}`.trim()
                    : undefined,
                  onClick: handleClick,
                  /*
                   * AB#21245 — was a filled box, one low-contrast lavender for all
                   * nineteen states. The column could be read but not scanned:
                   * "Mark to Factory" and "Customer Rejected" looked equally calm,
                   * and grey-on-grey for the most common state read as disabled.
                   * A dot carries the tone; the label keeps its own words.
                   */
                  class:
                    'inline-flex items-center gap-1.5 bg-transparent border-0 p-0 text-left whitespace-nowrap',
                  style: { color: TONE_TEXT[tone], cursor: showDropdown ? 'pointer' : 'default' },
                },
                [
                  h('span', {
                    class: 'inline-block rounded-full shrink-0',
                    style: { width: '7px', height: '7px', backgroundColor: TONE_DOT[tone] },
                  }),
                ].concat(
                  [
                    rejectedFlag
                      ? (CustimooOrderFlowStatuses[REJECTED_SAMPLE_STATUS] ?? 'Customer Rejected')
                      : (CustimooOrderFlowStatuses[row.status] ??
                        (row.status === 'on_hold' ? 'On Hold' : row.status)),
                    /*
                     * The chevron that opens "Assign Factory to Order".
                     *
                     * It used to be a <button> nested inside this button, carrying a
                     * duplicate of `handleClick`. Two consequences: a button inside a
                     * button is invalid HTML, and because the inner handler did not
                     * stop propagation, clicking the chevron ran BOTH handlers — so
                     * opening the modal fetched the order detail twice and set the
                     * modal state twice. The outer control already does the job, so
                     * the chevron is now the affordance and not a second control.
                     */
                    showDropdown &&
                      h('span', { class: 'inline-flex items-center pl-1' }, [
                        h(ChevronDownIcon, { class: 'text-ink-mute w-[16px] h-[16px]' }),
                      ]),
                  ].filter(Boolean)
                )
              ),
              complaintPill,
              rejectedFlag
                ? h(
                    'span',
                    { class: 'mo-rejected-bang', title: 'Sample rejected - needs attention' },
                    '!'
                  )
                : null,
              hasReadPermission || hasWritePermission
                ? h('div', { class: 'ml-2' }, [dqcCheckbox])
                : null,
            ].filter(Boolean)
          );
        },
      },
      {
        key: 'days_since_action',
        // AB#21242 — "Days Since Action" over "45 minutes ago" cost 150px to say
        // something the column is scanned for, not read. Same measurement, four
        // characters, full sentence on hover.
        label: 'Idle',
        title: 'Time since the last action on this order',
        cellClass: (row: DashboardItem) => {
          return selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '';
        },
        sortable: true,
        sortKey: 'status_updated_at',
        renderer: (val: any, row: DashboardItem) => {
          const updated = row.order.updated_at ?? new Date().toISOString();
          return h(
            'span',
            {
              class: 'whitespace-nowrap tabular-nums',
              title: timeAgo(updated),
            },
            compactDuration(updated)
          );
        },
      },
    ];

    // Conditionally add Amount column if permission is granted
    if (can('view-amount-column')) {
      baseColumns.splice(
        baseColumns.findIndex((col) => col.key === 'status'), // Insert before Status column
        0,
        {
          key: 'order_total_price',
          label: 'Amount',
          title: 'Order total',
          sortable: false,
          cellClass: (row: DashboardItem) => {
            return selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '';
          },
          renderer: (val: any, row: DashboardItem) => {
            const price = row.order_total_price;
            return price !== undefined && price !== null
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(price)
              : 'N/A';
          },
        }
      );
    }

    if (!openDetails.value) {
      return [
        ...baseColumns,
        canViewShippingAddress.value && {
          key: 'shipping_address',
          label: 'Shipping Address',
          cellClass: (row: DashboardItem) => {
            return selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '';
          },
          sortable: false,
          renderer: (val: any, row: DashboardItem) => {
            if (!row.order.shipping_address) return '';
            return `${row.order.shipping_address.address1 || ''} ${row.order.shipping_address.address2 || ''} ${row.order.shipping_address.city || ''}
                  ${row.order.shipping_address.zip_code || ''} ${row.order.shipping_address.country || ''}`;
          },
        },
        canAssignOrderAdmin.value && {
          key: 'admin',
          label: 'Administrator',
          title: 'Order Administrator',
          sortable: false,
          // AB#21242 — an inline select carrying its own min-width.
          noTruncate: true,
          cellClass: (row: DashboardItem) => {
            return selectedRow.value && selectedRow.value.id === row.id ? 'bg-surface-3' : '';
          },
          renderer: (val: any, row: DashboardItem) => {
            if (!row.factory_id && row.order.order_administrator?.name)
              return row.order.order_administrator.name;
            if (!row.factory_id) return h('span', { class: 'text-ink-mute' }, '—');
            const filteredOrderAdmins = orderAdmins.value.filter((admin: OrderAdminUser) => {
              // A Lead (and the Finance Controller and Order Admin) is offered without factory_id filtering
              if (isOfferedForEveryFactory(admin)) {
                return true;
              }
              // Otherwise, filter by factory_id
              return admin.factory_ids?.includes(row.factory_id);
            });
            return h(
              'div',
              {
                onClick: (e: Event) => e.stopPropagation(),
                class: 'w-full h-full',
              },
              [
                h(Select, {
                  options: filteredOrderAdmins,
                  modelValue: row.order.order_administrator,
                  'track-by': 'id',
                  label: 'name',
                  'onUpdate:modelValue': async (newValue: OrderAdminUser) => {
                    row.order.order_administrator = newValue;
                    const secondaryAdminIds: OrderAdminUser[] =
                      row?.order?.secondary_order_administrators
                        ?.map((item) => item.user)
                        .filter(Boolean)
                        .map((user) => user.id) || [];
                    const payload = {
                      user_id: newValue ? newValue.id : null,
                      order_ids: row.order.id,
                      secondary_administrator_ids: secondaryAdminIds,
                    };
                    await store.updateOrderAdmin(payload);

                    await fetchRecords({
                      ...defParams,
                      ...current_filters.value,
                    });
                  },
                  class: 'white-space-nowrap min-w-[200px] max-w-max',
                  placeholder: 'Assign Admin',
                  'append-to-body': true,
                }),
              ]
            );
          },
        },
      ].filter(Boolean);
    }

    return baseColumns;
  });

  // AB#19827 (Zendesk#7813) - add the rejected-row cue (red left edge + tint) to each cell of a
  // My Orders row whose sample was rejected, without touching the shared DataTable or other tabs.
  const decorateRejectedCue = (cols: any[]) =>
    (cols || []).map((col: any, idx: number) => {
      if (!col) return col;
      const baseCellClass = col.cellClass;
      return {
        ...col,
        cellClass: (row: DashboardItem) => {
          const base =
            typeof baseCellClass === 'function' ? baseCellClass(row) || '' : baseCellClass || '';
          if (isMyOrdersRejected(row)) {
            return `${base} mo-rejected-cell${idx === 0 ? ' mo-rejected-cell--first' : ''}`;
          }
          return base;
        },
      };
    });
  /**
   * AB#21240 — the user's column choice, applied last.
   *
   * It runs after the permission gates, never instead of them: a column the
   * user has no permission for is already absent from `visibleColumns`, so
   * hiding is only ever a subtraction from what they are allowed to see. The
   * picker offers whatever survives the gates, which is why the merchant view
   * offers seven entries and a SuperAdmin sixteen without a second code path.
   */
  /** AB#23060 — opt-in columns this user may see (Factory deadline). */
  const optInColumns = computed(() => defaultHiddenLabels(visibleColumns.value as any[]));
  /** What the table hides and the picker unticks: the user's own choice plus opt-in columns not switched on. */
  const hiddenColumns = computed(() =>
    effectiveHiddenColumns(storedHiddenColumns.value, shownColumns.value, optInColumns.value)
  );
  const setHiddenColumns = (labels: string[]) => {
    const { hidden, shown } = splitHiddenColumns(labels, optInColumns.value);
    // An opt-in column this user cannot see right now (a permission) keeps its choice.
    const keep = shownColumns.value.filter((label) => !optInColumns.value.includes(label));
    storedHiddenColumns.value = hidden;
    shownColumns.value = Array.from(new Set([...keep, ...shown]));
    writeHiddenColumns(localStorage, storedHiddenColumns.value);
    writeHiddenColumns(localStorage, shownColumns.value, shownColumnsKey());
  };
  /** "Reset columns to default": widths, hidden columns and the opt-in ones back off. */
  const resetColumns = () => {
    storedHiddenColumns.value = [];
    shownColumns.value = [];
    writeHiddenColumns(localStorage, []);
    writeHiddenColumns(localStorage, [], shownColumnsKey());
    resetColumnWidths();
  };

  const columns = computed(() =>
    decorateRejectedCue(applyColumnPreference(visibleColumns.value, hiddenColumns.value))
  );

  /** Labels the column picker should offer, in table order. */
  const columnOptions = computed(() => hideableLabels(visibleColumns.value));

  function hasAllValues(obj: Record<string, any>): boolean {
    return Object.values(obj).every(
      (value) =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0)
    );
  }
  const getQuickFilterData = async (filter: any) => {
    // Only jump back to page 1 when the filter set actually changes (a real
    // user action). When the same filters are merely re-applied — e.g. restored
    // from localStorage on page load/refresh — keep the page the user was on.
    const incomingFilters = filter?.quick_filters ?? {};
    const filtersUnchanged =
      JSON.stringify(incomingFilters) === JSON.stringify(dashboardQuickFilters.value ?? {});
    const pageToUse = filtersUnchanged ? (current_filters.value?.page ?? 1) : 1;
    // Preserve the user's current page size and active search when (re)applying
    // quick filters. Rebuilding from `defParams` alone would reset `per_page` to
    // the default and wipe `search` — the latter clears the search box on refresh
    // while the already-fetched results stay filtered (mismatched UI vs results).
    const savedPerPage = current_filters.value?.per_page ?? defParams.per_page;
    const savedSearch = current_filters.value?.search ?? defParams.search ?? '';
    if (filter && typeof filter === 'object' && hasAllValues(filter)) {
      const { quick_filters } = filter;
      defParams = {
        ...defParams,
        page: pageToUse,
        items_for: currentTab.value ?? 'all_items',
      };
      current_filters.value = { ...defParams, per_page: savedPerPage, search: savedSearch };
      localStorage.setItem(
        'dashboardRequest',
        JSON.stringify({ dashboardRequest: current_filters.value })
      );
      dashboardQuickFilters.value = quick_filters ? { ...quick_filters } : {};
    } else {
      defParams = {
        ...defParams,
        page: pageToUse,
        items_for: currentTab.value ?? 'all_items',
      };
      current_filters.value = { ...defParams, per_page: savedPerPage, search: savedSearch };
      localStorage.setItem(
        'dashboardRequest',
        JSON.stringify({ dashboardRequest: current_filters.value })
      );
      dashboardQuickFilters.value = {};
      selectedUrgentOrderFilter.value =
        filter?.urgent_order !== undefined ? filter?.urgent_order : selectedUrgentOrderFilter.value;
    }
  };

  watch(
    [() => dashboardStore.queryParams, () => dashboardStore.calendarFilterPayload],
    async ([newParams, newPayload], [oldParams, oldPayload]) => {
      if (currentMode.value === 'calendar') {
        if (newParams !== oldParams || newPayload !== oldPayload) {
          await dashboardStore.fetchCalendarData();
        }
      }
    },
    { immediate: true, deep: true }
  );
  // watch(() => [
  //   currentTab,
  //   selectedFilter,
  //   selectedUrgentOrderFilter,
  //   dashboardQuickFilters,
  //   defParams.sort_by,
  //   defParams.sort_order,
  //   defParams.search,
  //   current_filters.value.page], ([newVal]) => { console.log(newVal) })

  const currentOrderItem: ComputedRef<OrderDetailItem | null> = computed(() => {
    return selectedOrderDetails.value
      ? selectedOrderDetails.value.items.find((item) => item.id === currentRow.value?.id)
      : null;
  });

  const currentOrderItemIndex: ComputedRef<number | null> = computed(() => {
    return selectedOrderDetails.value
      ? selectedOrderDetails.value.items.findIndex((item) => item.id === currentRow.value?.id)
      : null;
  });

  /** "#27951", "M_#27950", "#27949-1" — what the row shows, so the tab matches it. */
  const tabLabelFor = (
    row: DashboardItem | null | undefined,
    details?: OrderDetail | null
  ): string => {
    const itemNo = (row as any)?.item_no ?? (details as any)?.order_no;
    if (!itemNo) return '';
    const manual = Boolean(
      (row as any)?.order?.additional_fields?.is_manual_order ??
      (details as any)?.additional_fields?.is_manual_order
    );
    return `${manual ? 'M_' : ''}#${itemNo}`;
  };
  const tabMerchantFor = (
    row: DashboardItem | null | undefined,
    details?: OrderDetail | null
  ): string => String((row as any)?.order?.company_name ?? (details as any)?.company_name ?? '');

  /**
   * Show one tab's order in the pane: from the cache when it is fresh, else
   * fetched. `selectedOrderDetails` / `currentRow` are what the rest of the
   * page reads, so they are set here and only here when the active tab changes.
   */
  const showTab = async (key: string) => {
    const tab = orderTabs.value.tabs.find((entry) => entry.key === key);
    if (!tab) return;

    const cachedRow = rowsByTab.get(key);
    const cached = detailsByTab.get(key);
    const fresh = cached && Date.now() - cached.fetchedAt < DETAIL_TTL_MS;

    if (cachedRow) currentRow.value = cachedRow;
    if (cached) selectedOrderDetails.value = cached.details;
    setPageTitle(tab.label || undefined);
    paneDefaultTab.value = tab.innerTab;

    if (fresh && cachedRow) return;

    isSidepaneLoading.value = true;
    try {
      const fetched = await store.getOrderDetail(tab.orderId, tab.itemId);
      // Carry the list row's complaints onto the detail, as the row click does.
      const details = fetched ? mergeComplaintsOntoOrderDetail(fetched, cachedRow?.order ?? null) : fetched;
      // The user may have moved on while this was in flight.
      if (orderTabs.value.activeKey !== key) {
        if (details) detailsByTab.set(key, { details, fetchedAt: Date.now() });
        return;
      }
      if (details) {
        selectedOrderDetails.value = details;
        detailsByTab.set(key, { details, fetchedAt: Date.now() });
        if (!cachedRow) {
          const row = rowForRestoredPane(tableData.value, details, tab.itemId);
          if (row) {
            rowsByTab.set(key, row);
            currentRow.value = row;
          }
        }
        // A tab restored from storage, or opened by URL, may not know its
        // own name yet.
        if (!tab.label || !tab.merchant) {
          orderTabs.value = {
            ...orderTabs.value,
            tabs: orderTabs.value.tabs.map((entry) =>
              entry.key === key
                ? {
                    ...entry,
                    label: entry.label || tabLabelFor(rowsByTab.get(key), details),
                    merchant: entry.merchant || tabMerchantFor(rowsByTab.get(key), details),
                  }
                : entry
            ),
          };
          setPageTitle(orderTabs.value.tabs.find((entry) => entry.key === key)?.label || undefined);
        }
      }
    } finally {
      if (orderTabs.value.activeKey === key) isSidepaneLoading.value = false;
    }
  };

  const clearReplaceUndo = () => {
    if (lastReplacedTimer) clearTimeout(lastReplacedTimer);
    lastReplacedTimer = null;
    lastReplacedTab.value = null;
  };

  const handleCellClick = async (
    // `column` is part of the table's cell-click payload but this handler has
    // never used it — it opens the whole row. Optional so the row ⋯ menu's
    // "Details" can open the same pane with just a row (AB#21587 follow-up),
    // instead of the old link out to the V1 order page.
    { row }: { row: DashboardItem; column?: Column },
    options?: { fromRestore?: boolean }
  ) => {
    if (!options?.fromRestore) {
      // Bug 20628 (Zendesk #8494) — an explicit row pick makes any pending saved-row restore
      // obsolete. Clearing both the ids and the latch stops a later orders refetch (e.g. the one
      // `fetchOnSave` triggers) from re-targeting the dashboard to a previously handled order while
      // the user is working on this one. `fromRestore` keeps the restore path itself untouched so it
      // still scrolls to the restored row via the `selectedRow` computed.
      savedRowIds.value = null;
      fetchOrder.value = false;
    }

    // AB#21279 — a row click opens a tab. Already open: switch to it. Full:
    // replace the tab viewed longest ago, unless it has unsaved input; with
    // every tab dirty the click is refused and nothing is lost.
    const key = tabKey(row.order.id, row.id);
    const opened = openTab(
      orderTabs.value,
      {
        orderId: row.order.id,
        itemId: row.id,
        label: tabLabelFor(row),
        merchant: tabMerchantFor(row),
        innerTab: deskDefaultTab(),
      },
      Date.now()
    );
    if (!opened.ok) {
      showToast('All five open orders have unsaved changes. Close one first.', 'warning');
      return;
    }
    if (opened.replaced) {
      // Keep the displaced tab's details so Undo is instant; drop them when the
      // offer expires.
      clearReplaceUndo();
      lastReplacedTab.value = { tab: opened.replaced, byKey: key };
      lastReplacedTimer = setTimeout(() => {
        detailsByTab.delete(opened.replaced!.key);
        rowsByTab.delete(opened.replaced!.key);
        lastReplacedTab.value = null;
      }, 8000);
    }
    rowsByTab.set(key, row);
    orderTabs.value = opened.state;

    localStorage.setItem('openDetails', 'true');
    openDetails.value = true;
    // AB#21240 — remember what the menu was before the pane took the space, so
    // closing the pane can put it back rather than forcing it open.
    if (sidebarStateBeforePane.value === null) sidebarStateBeforePane.value = sidebarOpen.value;
    sidebarOpen.value = false;

    await showTab(key);
  };

  /** Click on a tab in the strip. */
  const activateOrderTab = async (key: string) => {
    if (key === orderTabs.value.activeKey) return;
    orderTabs.value = activateTab(orderTabs.value, key, Date.now());
    await showTab(key);
  };

  /** Put back the tab a sixth order displaced. */
  const undoLastReplace = async () => {
    const pending = lastReplacedTab.value;
    if (!pending) return;
    clearReplaceUndo();
    const restored = undoReplace(orderTabs.value, pending.tab, pending.byKey);
    if (restored === orderTabs.value) return;
    detailsByTab.delete(pending.byKey);
    rowsByTab.delete(pending.byKey);
    orderTabs.value = restored;
    await showTab(pending.tab.key);
  };

  /** OrderDetail reports whether its move-status form holds unsaved input. */
  const setActiveTabDirty = (dirty: boolean) => {
    const key = orderTabs.value.activeKey;
    if (!key) return;
    orderTabs.value = setDirty(orderTabs.value, key, dirty);
  };

  /**
   * The × on the strip: every tab, and the pane with them. Asks once if any
   * tab holds unsaved input, naming how many.
   */
  const closeAllOrderTabs = async () => {
    const dirty = orderTabs.value.tabs.filter((tab) => tab.dirty);
    if (dirty.length > 0) {
      const yes = await showConfirm(
        dirty.length === 1
          ? `Close ${dirty[0].label}?`
          : `Close all ${orderTabs.value.tabs.length} orders?`,
        dirty.length === 1
          ? 'This order has unsaved changes in the move-status form. They will be lost.'
          : `${dirty.length} of them have unsaved changes in the move-status form. They will be lost.`
      );
      if (!yes) return;
    }
    onDetailsClose();
  };

  const togglePaneExpanded = (value?: boolean) => {
    paneExpanded.value = value ?? !paneExpanded.value;
    localStorage.setItem('dashboardPaneExpanded', paneExpanded.value ? 'true' : 'false');
  };

  /**
   * Close one tab. A dirty tab asks first. Closing the last tab closes the
   * pane, which is what `onDetailsClose` has always done.
   */
  const closeOrderTab = async (key: string) => {
    const tab = orderTabs.value.tabs.find((entry) => entry.key === key);
    if (!tab) return;
    if (tab.dirty) {
      const yes = await showConfirm(
        `Close ${tab.label}?`,
        'This order has unsaved changes in the move-status form. They will be lost.'
      );
      if (!yes) return;
    }
    const wasActive = orderTabs.value.activeKey === key;
    const next = closeTab(orderTabs.value, key);
    detailsByTab.delete(key);
    rowsByTab.delete(key);
    orderTabs.value = next;

    if (next.tabs.length === 0) {
      onDetailsClose();
      return;
    }
    if (wasActive && next.activeKey) await showTab(next.activeKey);
  };

  // Open the side-pane on the timeline tab and scroll to the latest
  // complaint card after the timeline mounts.
  const handleComplaintPillClick = async (row: DashboardItem) => {
    paneDefaultTab.value = 'timeline';
    localStorage.setItem('activeDetailsTab', 'timeline');
    await handleCellClick({ row, column: { key: 'complaint', label: 'complaint' } });

    const latest = getComplaintsArray(row.order)[0];
    if (!latest) return;
    const complaintId = latest.id ?? latest.ticket_no ?? '';
    if (!complaintId) return;

    nextTick(() => {
      setTimeout(() => {
        const el = document.getElementById(`complaint-card-${complaintId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 250);
    });
  };

  const handleOrderMoved = async () => {
    isSidepaneLoading.value = true;

    try {
      if (currentRow.value) {
        const orderData = await store.getOrderDetail(
          currentRow.value.order.id,
          currentRow.value.id
        );
        selectedOrderDetails.value = mergeComplaintsOntoOrderDetail(
          orderData,
          currentRow.value.order
        );
      }

      await fetchOnSave(true, true);
    } catch (error) {
      logError('Error refetching order details:', error);
    } finally {
      isSidepaneLoading.value = false;
    }
  };

  const handleOrderUpdated = async () => {
    isSidepaneLoading.value = true;

    try {
      if (currentRow.value) {
        const orderData = await store.getOrderDetail(
          currentRow.value.order.id,
          currentRow.value.id
        );
        selectedOrderDetails.value = mergeComplaintsOntoOrderDetail(
          orderData,
          currentRow.value.order
        );
      }

      await fetchOnSave(true, true);
    } catch (error) {
      logError('Error refetching order details:', error);
    } finally {
      isSidepaneLoading.value = false;
    }
  };

  const openInternalNoteModal = (row) => {
    internalNoteRow.value = row;
    internalNoteModal.value = true;
  };

  const handleInternalNoteClose = async () => {
    internalNoteModal.value = false;
    internalNoteRow.value = null;
    showDashboardActions.value = false;
    await fetchOnSave(true);
  };

  const scrollToCurrentRow = () => {
    const localRow = currentRow.value ?? previousSelectedRow.value;
    if (!localRow || !datatable.value) return;

    nextTick(() => {
      try {
        const tableElement = datatable.value.$el;
        const tableBody = tableElement?.querySelector('tbody');

        if (!tableBody) return;

        const rows = tableBody.querySelectorAll('tr');
        let targetRow: HTMLElement | null = null;

        rows.forEach((row) => {
          const rowData = row.getAttribute('data-row-id') || row.dataset.rowId;
          if (rowData === localRow.id.toString()) {
            targetRow = row;
          }
        });
        if (targetRow) {
          targetRow.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
          targetRow.style.transition = 'background-color 0.3s ease';
          targetRow.style.backgroundColor = '#e3f2fd';
          setTimeout(() => {
            targetRow.style.backgroundColor = '';
          }, 1000);
        }
      } catch (error) {
        console.warn('Error scrolling to selected row:', error);
      }
    });
  };

  const openOrderPriceModal = async () => {
    const orderData = await store.getOrderDetail(currentRow.value.order.id,currentRow.value.id);
    selectedOrderDetails.value = mergeComplaintsOntoOrderDetail(
      orderData,
      currentRow.value?.order ?? null
    );
    priceBreakdownModal.value = true;
    await invalidateOrderActivityLogQueries();
  };

  const onDetailsClose = () => {
    selectedOrderDetails.value = null;
    previousSelectedRow.value = { ...currentRow.value };
    localStorage.removeItem('currentRow');
    currentRow.value = null;
    // AB#21279 — the pane closing closes every tab. Reached from the strip
    // only when the last tab goes; other callers (a deleted order, say) still
    // mean "the pane is done".
    orderTabs.value = { tabs: [], activeKey: null };
    detailsByTab.clear();
    rowsByTab.clear();
    clearReplaceUndo();
    togglePaneExpanded(false);
    /**
     * AB#21240 — was `sidebarOpen.value = true`.
     *
     * Opening an order collapses the menu to make room; this put it back
     * expanded regardless of what the user had chosen, so anyone who
     * deliberately collapsed it got it forced open again the moment they closed
     * an order. Restore the state the pane displaced instead, falling back to
     * the user's stored preference when the pane was restored on page load and
     * there is nothing to displace.
     */
    sidebarOpen.value = sidebarStateAfterPaneClose(sidebarStateBeforePane.value, localStorage);
    sidebarStateBeforePane.value = null;
    openDetails.value = false;
    localStorage.setItem('openDetails', 'false');
    setPageTitle();
  };
  const detailsTabChange = (tabId: string) => {
    paneDefaultTab.value = tabId;
    localStorage.setItem('activeDetailsTab', tabId ?? 'timeline');
    // AB#21279 — remembered per tab, so switching orders brings back the
    // inner tab each one was on.
    if (orderTabs.value.activeKey) {
      orderTabs.value = setInnerTab(
        orderTabs.value,
        orderTabs.value.activeKey,
        tabId ?? 'timeline'
      );
    }
  };
  const handleRowSelected = (row: DashboardItem | null) => {
    // Bug 20628 (Zendesk #8494) — `DashboardActions.handlePriceBreakDownModalCloseEvent` emits
    // `selectRow` with `null` when the Price Breakdown modal closes, and the row-actions host binds
    // this handler directly to that event. Without the guard the dereference below throws.
    if (!row?.order?.id) return;
    // Save only order_id and orderItem id instead of full row
    localStorage.setItem('currentRow', JSON.stringify({ order_id: row.order.id, id: row.id }));
    if (!openDetails.value) {
      localStorage.setItem('openDetails', 'false');
      openDetails.value = false;
    }

    currentActiveRow.value = row;
  };
  const openDashboardActions = async ({ event, row }: { event: MouseEvent; row: any }) => {
    event.preventDefault();

    const { clientX, clientY } = event;

    if (popperInstance) {
      popperInstance.destroy();
      popperInstance = null;
    }

    window.virtualReference = {
      getBoundingClientRect: () => new DOMRect(clientX, clientY, 0, 0),
    };
    showDashboardActions.value = true;

    // Bug 20628 (Zendesk #8494) — same reasoning as `handleCellClick`: right-clicking a row is an
    // explicit pick, so any pending saved-row restore is obsolete and must not fire on a later refetch.
    savedRowIds.value = null;
    fetchOrder.value = false;

    // Save only order_id and orderItem id instead of full row
    localStorage.setItem('currentRow', JSON.stringify({ order_id: row.order.id, id: row.id }));
    currentRow.value = row;

    if (currentRow.value) {
      localStorage.setItem('openDetails', 'true');

      if (openDetails.value) {
        sidebarOpen.value = false;
      }

      await nextTick();

      if (dashboardActionsRef.value && window.virtualReference) {
        popperInstance = createPopper(window.virtualReference, dashboardActionsRef.value, {
          placement: 'bottom-start',
          strategy: 'fixed',
          modifiers: [
            { name: 'offset', options: { offset: [0, 6] } },
            { name: 'preventOverflow', options: { boundary: 'viewport' } },
            {
              name: 'flip',
              options: { fallbackPlacements: ['top-start', 'right-start', 'left-start'] },
            },
          ],
        });
      }

      try {
        const orderData = await store.getOrderDetail(row.order.id, row.id);
        selectedOrderDetails.value = mergeComplaintsOntoOrderDetail(orderData, row.order);
      } finally {
        isSidepaneLoading.value = false;
      }
    } else {
      localStorage.setItem('openDetails', 'false');
      openDetails.value = false;
    }
  };

  const handleCloseAction = () => {
    showDashboardActions.value = false;
    if (popperInstance) {
      popperInstance.destroy();
      popperInstance = null;
    }
  };

  // AB#20682: third copy of this logic in admin_v2 — delegates to the shared
  // helper. This one only rendered the non-possible branch, which is why a
  // grep for "Possible reorder" did not surface it.
  const makeReorderMessage = (factory_product: FactoryProduct) =>
    sharedMakeReorderMessage(factory_product as unknown as ReorderFactoryProduct);

  const filterByIncludes = (filterBy: unknown, value: string): boolean => {
    if (filterBy == null) return false;
    if (Array.isArray(filterBy)) return filterBy.includes(value);
    return filterBy === value;
  };

  const toggleDQC = async (event: Event, orderId, rowId) => {
    const target = event.target as HTMLInputElement;
    const intendedStatus = target.checked;
    try {
      // Proceed only if confirmed
      target.checked = intendedStatus;
      const payload = {
        dqc_approved: intendedStatus,
        user_id: currentUser.value?.id,
        name: currentUser.value?.name,
      };
      await store.updateOrderDQCStatus(orderId, payload);

      if (
        filterByIncludes(dashboardQuickFilters.value?.filter_by, 'marked_dqc') ||
        filterByIncludes(dashboardQuickFilters.value?.filter_by, 'not_marked_dqc')
      ) {
        await fetchOnSave(true);
      } else {
        const orderData = await store.getOrderDetail(orderId);
        const updated = data.value.items.find((row) => row.id === rowId);
        if (updated && updated.order) {
          updated.order.mark_dqc = orderData.mark_dqc;
          updated.order.dqc_user_name = orderData.dqc_user_name;
          updated.order.dqc_marked_at = orderData.dqc_marked_at;
        }
      }
    } catch (error) {
      console.error('Error updating DQC status:', error);
      target.checked = !intendedStatus;
    } finally {
      // No additional action needed here since we handle state in try/catch
    }
  };

  const exportOrderPrices = async (
    fromDate: string,
    endDate: string,
    exportType: string = 'order_item',
    groupBy: string[] = []
  ): Promise<void> => {
    // AB#21520 — built from the live filters, and deliberately NOT scoped to the
    // open tab: `items_for` is a status whitelist on the wire, and every tab
    // whose whitelist omits `order_approve` was silently dropping the Mark to
    // Factory orders from the file. See buildExportOrderParams for the whole
    // story. The one-shot deep-link `order_id` is stripped there too, so an
    // export can never collapse to the single order an email link opened.
    const params: DashboardRequest = buildExportOrderParams(current_filters.value);

    await store.exportOrderPrices(params, dashboardQuickFilters.value, {
      fromDate: fromDate,
      endDate: endDate,
      exportType: exportType,
      groupBy: groupBy,
    });
  };

  const getShippmentBookingCsv = async () => {
    try {
      isDisabled.value = true;
      const response = await http.get('/file/shippment-csv');

      const downloadUrl = response.data.result.url;

      if (!downloadUrl) {
        isDisabled.value = false;
        showToast('This file has no download link. Reload the page and try again.', 'error');
        return;
      }

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'shipment_report.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      isDisabled.value = false;
    } catch (error) {
      isDisabled.value = false;
      showToast("There's no shipments file for this order yet.", 'error');
    }
  };

  // watch([selectedOrderDetails], () => {
  //   if (currentRow.value) {
  //     scrollToCurrentRow();
  //   }
  // });
  // AB#21279 — the set survives a refresh; the legacy single-order keys are
  // kept in step for the active tab because older code still reads them.
  watch(
    orderTabs,
    (state) => {
      writeTabs(localStorage, state, currentUser.value?.id ?? null);
      const active = activeOrderTab(state);
      if (active) {
        localStorage.setItem(
          'currentRow',
          JSON.stringify({ order_id: active.orderId, id: active.itemId })
        );
        localStorage.setItem('activeDetailsTab', active.innerTab);
      }
    },
    { deep: true }
  );
  // Anything that refreshes the pane's order (a move, a save) refreshes the
  // active tab's cache too — but only when it IS the active tab's order. The
  // row context menu also writes selectedOrderDetails, for a row that may not
  // be open at all.
  watch(selectedOrderDetails, (details) => {
    const active = activeOrderTab(orderTabs.value);
    if (details && active && details.id === active.orderId) {
      detailsByTab.set(active.key, { details, fetchedAt: Date.now() });
    }
  });
  /** Item ids of every open tab, for marking their rows in the table. */
  const openOrderItemIds = computed(() => new Set(orderTabs.value.tabs.map((tab) => tab.itemId)));

  return {
    appURL,
    dashboardTabs,
    // AB#21279
    orderTabs,
    openOrderItemIds,
    activateOrderTab,
    closeOrderTab,
    closeAllOrderTabs,
    undoLastReplace,
    lastReplacedTab,
    setActiveTabDirty,
    paneExpanded,
    togglePaneExpanded,
    permissionLoaded,
    datatable,
    currentTab,
    createManualOrder,
    assignFactoryModal,
    selectedOrderDetails,
    isSidepaneLoading,
    priceBreakdownModal,
    selectedOrderItem,
    internalNoteModal,
    internalNoteRow,
    currentRow,
    current_filters,
    filteredOrderDetailTabs,
    filteredModeTabs,
    orderOptions,
    tableData,
    totalItems,
    columns,
    // AB#21240
    columnOptions,
    hiddenColumns,
    setHiddenColumns,
    // AB#21576
    columnWidths,
    setColumnWidths,
    resetColumnWidths,
    resetColumns,
    canResetColumns,
    applySidebarPreference,
    pageSizeOptions: DEFAULT_PAGE_SIZES,
    currentOrderItem,
    currentOrderItemIndex,
    error,
    isFetching,
    isPending,
    currentMode,
    dashboardStore,
    sidebarOpen,
    dashboardData,
    paneDefaultTab,
    pendingAction,
    clearPendingAction,
    taskSearchQuery,
    isDisabled,
    getStatusColor,
    handlePriceBreakDownModalCloseEvent,
    onOrderSearch,
    onModeTabChange,
    handleViewChanged,
    onCalendarFilterChange,
    factoryModalClose,
    fetchRecords,
    onTabChange,
    fetchOnSave,
    getQuickFilterData,
    handleCellClick,
    handleOrderMoved,
    handleOrderUpdated,
    handleInternalNoteClose,
    openOrderPriceModal,
    onDetailsClose,
    detailsTabChange,
    handleRowSelected,
    currentActiveRow,
    openDashboardActions,
    showDashboardActions,
    dashboardActionsRef,
    openDetails,
    handleCloseAction,
    selectedRow,
    makeReorderMessage,
    exportOrderPrices,
    getShippmentBookingCsv,
  };
};
